import PropTypes from 'prop-types';
import IncidentCard from '../IncidentCard';
import { calculateRiskScore, calculateTrend } from '@/utils/riskScoring';

/**
 * Entra ID Security View Component for SOC Dashboard
 * Displays Microsoft Entra ID (Azure AD) identity security status
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Entra ID security data
 * @param {Object} props.previousData - Previous period data for trend calculation
 */
const EntraIDSecurityView = ({ data, previousData }) => {
  if (!data) {
    return (
      <div className="soc-view entra-security-view">
        <h2 className="view-title">Entra ID (Azure AD) Security</h2>
        <p className="no-data-message">Loading Entra ID data...</p>
      </div>
    );
  }

  // Calculate risk scores
  const riskyUsersScore = calculateRiskScore({
    fileCount: data.entraRiskyUserCount || 0
  });

  const riskySignInsScore = calculateRiskScore({
    fileCount: data.entraRiskySignInCount || 0,
    uniqueIPCount: data.entraRiskySignInUniqueIPs || 0
  });

  const anomalousLogonsScore = calculateRiskScore({
    fileCount: data.entraAnomalousLogonCount || 0,
    failedAttempts: data.entraAnomalousLogonFailed || 0,
    uniqueIPCount: data.entraAnomalousLogonUniqueIPs || 0
  });

  // Calculate trends
  const riskyUsersTrend = previousData ? calculateTrend(
    data.entraRiskyUserCount || 0,
    previousData.entraRiskyUserCount || 0
  ) : null;

  const riskySignInsTrend = previousData ? calculateTrend(
    data.entraRiskySignInCount || 0,
    previousData.entraRiskySignInCount || 0
  ) : null;

  const anomalousLogonsTrend = previousData ? calculateTrend(
    data.entraAnomalousLogonCount || 0,
    previousData.entraAnomalousLogonCount || 0
  ) : null;

  const totalEntraRisks = (data.entraRiskyUserCount || 0) + 
                         (data.entraRiskySignInCount || 0) + 
                         (data.entraAnomalousLogonCount || 0);

  return (
    <div className="soc-view entra-security-view">
      <h2 className="view-title">Entra ID (Azure AD) Security</h2>

      {/* Summary Header */}
      <div className="view-summary-header">
        <div className="summary-stat">
          <span className="summary-label">Total Identity Risks</span>
          <span className="summary-value">{totalEntraRisks}</span>
        </div>
        <div className="summary-stat">
          <span className="summary-label">Total Users</span>
          <span className="summary-value">{data.entraTotalUsers || 0}</span>
        </div>
        <div className="summary-stat">
          <span className="summary-label">Sign-Ins Monitored</span>
          <span className="summary-value">{data.entraTotalSignIns || 0}</span>
        </div>
      </div>

      {/* Entra ID Alert Cards */}
      <div className="incident-cards-grid">
        <IncidentCard
          title="Risky Users"
          riskScore={riskyUsersScore}
          data={{
            count: data.entraRiskyUserCount || 0,
            highRiskUsers: data.entraRiskyUserHigh || 0,
            mediumRiskUsers: data.entraRiskyUserMedium || 0,
            compromisedUsers: data.entraRiskyUserCompromised || 0
          }}
          incidentType="EntraID"
          trend={riskyUsersTrend}
          expandable={true}
        />

        <IncidentCard
          title="Risky Sign-Ins"
          riskScore={riskySignInsScore}
          data={{
            count: data.entraRiskySignInCount || 0,
            suspiciousLocations: data.entraRiskySignInSuspiciousLocations || 0,
            anonymousIPs: data.entraRiskySignInAnonymous || 0,
            uniqueIPs: data.entraRiskySignInUniqueIPs || 0
          }}
          incidentType="EntraID"
          trend={riskySignInsTrend}
          expandable={true}
        />

        <IncidentCard
          title="Anomalous Logons"
          riskScore={anomalousLogonsScore}
          data={{
            count: data.entraAnomalousLogonCount || 0,
            failedAttempts: data.entraAnomalousLogonFailed || 0,
            impossibleTravel: data.entraAnomalousLogonImpossibleTravel || 0,
            uniqueIPs: data.entraAnomalousLogonUniqueIPs || 0
          }}
          incidentType="EntraID"
          trend={anomalousLogonsTrend}
          expandable={true}
        />
      </div>

      {/* Risk Detection Types */}
      <div className="view-details-section">
        <h3>Risk Detection Breakdown</h3>
        <div className="risk-detection-grid">
          <div className="detection-item">
            <span className="detection-label">Anonymous IP Addresses</span>
            <span className="detection-value">{data.entraDetectionAnonymousIP || 0}</span>
          </div>
          <div className="detection-item">
            <span className="detection-label">Unfamiliar Locations</span>
            <span className="detection-value">{data.entraDetectionUnfamiliarLocation || 0}</span>
          </div>
          <div className="detection-item">
            <span className="detection-label">Malware Linked IPs</span>
            <span className="detection-value">{data.entraDetectionMalwareIP || 0}</span>
          </div>
          <div className="detection-item">
            <span className="detection-label">Impossible Travel</span>
            <span className="detection-value">{data.entraDetectionImpossibleTravel || 0}</span>
          </div>
          <div className="detection-item">
            <span className="detection-label">Password Spray</span>
            <span className="detection-value">{data.entraDetectionPasswordSpray || 0}</span>
          </div>
          <div className="detection-item">
            <span className="detection-label">Leaked Credentials</span>
            <span className="detection-value">{data.entraDetectionLeakedCreds || 0}</span>
          </div>
        </div>
      </div>

      {/* Conditional Access Policies Status */}
      <div className="view-details-section">
        <h3>Conditional Access Policy Status</h3>
        <div className="policy-status-grid">
          <div className="policy-stat-item">
            <span className="policy-stat-label">Total Policies</span>
            <span className="policy-stat-value">{data.entraConditionalAccessPolicies || 0}</span>
          </div>
          <div className="policy-stat-item">
            <span className="policy-stat-label">Policy Successes</span>
            <span className="policy-stat-value success">{data.entraConditionalAccessSuccess || 0}</span>
          </div>
          <div className="policy-stat-item">
            <span className="policy-stat-label">Policy Failures</span>
            <span className="policy-stat-value failure">{data.entraConditionalAccessFailure || 0}</span>
          </div>
          <div className="policy-stat-item">
            <span className="policy-stat-label">MFA Challenges</span>
            <span className="policy-stat-value">{data.entraMFAChallenges || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

EntraIDSecurityView.propTypes = {
  data: PropTypes.object,
  previousData: PropTypes.object
};

EntraIDSecurityView.defaultProps = {
  data: null,
  previousData: null
};

export default EntraIDSecurityView;
